import type { AdviceCard } from '../types/adviceCards.types'
import { ELITE_PLANS_ELITE_PRIME_TAB_ID } from '../constants/planIds'
import { useElitePlansLanding } from '../ElitePlansLandingContext'
import { ElitePlansIcon } from '../icons/iconRegistry'
import { resolveAdviceCardsForPlan } from './advice-cards/adviceCardCatalog'
import { renderAdviceCard } from './advice-cards/renderAdviceCard'
import { ElitePlansAdviceCarouselSkeleton } from './ElitePlansAdviceCarouselSkeleton'
import { shouldShowElitePlansRebalancingPlanSection } from './ElitePlansRebalancingPlanSection'
import { ElitePlansSectionSeparator } from './ElitePlansSectionSeparator'
import { resolveInsightBannerDisplay } from './resolveInsightBannerDisplay'

type ElitePlansAdviceCarouselProps = {
  /**
   * When true (sell body), Elite may show a leading separator if SEBI rebalancing
   * is mounted above. Dashboards never mount SEBI — pass false so funds-to-rebalance
   * alone does not invent a separator (`/elite-member-dashboard`, member tab, etc.).
   */
  rebalancingSectionAbove?: boolean
}

export function ElitePlansAdviceCarousel({
  rebalancingSectionAbove = true
}: ElitePlansAdviceCarouselProps) {
  const { activeTab, isStatusLoading, status, fundsToRebalanceFallback } = useElitePlansLanding()
  const insightBanner = activeTab.insightBanner
  // Card set is product catalog + insights gates — not CMS adviceCards list
  const adviceCards = resolveAdviceCardsForPlan(activeTab.id, activeTab.adviceCards)
  if (!adviceCards.length && !insightBanner) return null

  const showRebalancingAbove =
    rebalancingSectionAbove &&
    shouldShowElitePlansRebalancingPlanSection({
      activeTabId: activeTab.id,
      status,
      isStatusLoading,
      fundsToRebalanceFallback
    })
  // Elite: separator only when SEBI rebalancing is above; Prime/allocation keep their separator
  const leadingSeparator =
    showRebalancingAbove ||
    activeTab.id === ELITE_PLANS_ELITE_PRIME_TAB_ID ||
    activeTab.allocationPreview ? (
      <ElitePlansSectionSeparator />
    ) : null

  if (isStatusLoading) {
    return (
      <>
        {leadingSeparator}
        <ElitePlansAdviceCarouselSkeleton />
      </>
    )
  }

  const bannerDisplay = insightBanner
    ? resolveInsightBannerDisplay({
        insightBanner,
        strategyStatus: status?.elitePrimeInsights?.strategyData?.status,
        resolveByStrategy: activeTab.id === ELITE_PLANS_ELITE_PRIME_TAB_ID
      })
    : null

  return (
    <>
      {leadingSeparator}
      <section>
        <div className='px-5'>
          {bannerDisplay?.statusLabel ? (
            <p
              className='flex items-center justify-center gap-1.5 text-13 font-medium text-[color:var(--ep-accent)] mb-1'
              style={
                bannerDisplay.statusColor
                  ? { color: bannerDisplay.statusColor }
                  : undefined
              }
            >
              {bannerDisplay.showReadyIcon ? (
                <ElitePlansIcon
                  iconKey='checkReady'
                  fill={bannerDisplay.statusColor ?? 'currentColor'}
                />
              ) : null}
              {bannerDisplay.statusLabel}
            </p>
          ) : null}
          {bannerDisplay?.title ? (
            <h2 className='whitespace-pre-line text-center text-[20px] font-normal leading-[1.3] text-grey10'>
              {bannerDisplay.title}
            </h2>
          ) : null}
        </div>

        {adviceCards.length > 0 ? (
          <div className='mt-6 -mx-0'>
            <ul className='flex list-none snap-x snap-mandatory gap-3 overflow-x-auto overflow-y-hidden px-5 pb-1 no-scrollbar [scroll-padding-inline:20px]'>
              {adviceCards.map((card) => (
                <AdviceCardEntry key={card.id} card={card} />
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </>
  )
}

function AdviceCardEntry({ card }: { card: AdviceCard }) {
  return renderAdviceCard(card)
}
